import { Tag } from 'antd'
import { useTranslation } from 'react-i18next'

import type { OrderSummaryRoot } from '@repo/sdk'

interface OrderTypeBadgeProps {
  orderType: OrderSummaryRoot['orderType']
  auctionType?: OrderSummaryRoot['auctionType']
  /**
   * Server-decided sale type. The only thing that tells a marketplace purchase from a broadcast
   * instant purchase — both are `INSTANT_PURCHASE` orders. Order briefs (settlement detail) carry
   * it without `auctionType`, so an auction sale type also supplies the subtype.
   */
  saleType?: OrderSummaryRoot['saleType'] | null
  style?: React.CSSProperties
}

const AUCTION_TYPE_BY_SALE_TYPE: Partial<Record<string, string>> = {
  GENERAL_AUCTION: 'GENERAL',
  FIRST_COME_AUCTION: 'FIRST_COME',
  BLIND_AUCTION: 'BLIND',
}

const orderTypeColors = {
  AUCTION: 'purple',
  INSTANT_PURCHASE: 'blue',
  GROUP_BUY: 'cyan',
  GIVEAWAY: 'magenta',
} as const

const AUCTION_TYPE_LABEL_KEYS: Record<string, string> = {
  GENERAL: 'common.orderTypes.generalAuction',
  FIRST_COME: 'common.orderTypes.firstComeAuction',
  BLIND: 'common.orderTypes.blindAuction',
}

export function OrderTypeBadge({
  orderType,
  auctionType,
  saleType,
  style,
}: OrderTypeBadgeProps): React.JSX.Element {
  const { t } = useTranslation()

  const orderTypeLabels = {
    AUCTION: t('common.orderTypes.auction'),
    INSTANT_PURCHASE: t('common.orderTypes.instantPurchase'),
    GROUP_BUY: t('common.orderTypes.groupBuy'),
    GIVEAWAY: t('common.orderTypes.giveaway'),
  } as const

  const isMarketplace = saleType === 'MARKETPLACE'
  const effectiveAuctionType =
    auctionType ?? (saleType ? AUCTION_TYPE_BY_SALE_TYPE[saleType] : undefined)
  const auctionLabelKey =
    orderType === 'AUCTION' && effectiveAuctionType
      ? AUCTION_TYPE_LABEL_KEYS[effectiveAuctionType]
      : undefined
  const label = isMarketplace
    ? t('common.orderTypes.marketplace')
    : auctionLabelKey
      ? t(auctionLabelKey)
      : orderTypeLabels[orderType]

  return (
    <Tag color={isMarketplace ? 'geekblue' : orderTypeColors[orderType]} style={style}>
      {label}
    </Tag>
  )
}
